// EMPREGO DO TECNICO: pressao, demissao e o mercado de treinadores.
//
// Isto existe na vida real e e frequente. A NBA demite meia duzia de tecnicos
// por temporada, boa parte no MEIO dela, com um interino terminando o ano -- e
// quase todos os demitidos reaparecem em outro banco depois. O NBB tem o mesmo
// ciclo, com menos cobertura.
//
// Duas diferencas em relacao ao futebol que mudam o desenho:
//
//  1. A expectativa e do ELENCO, nao do escudo. Um time em reconstrucao nao
//     demite por perder: demite por nao desenvolver. Por isso a meta sai da
//     forca do elenco, e nao do tamanho do clube.
//  2. Demitido no meio da temporada, o tecnico raramente assume outro time no
//     mesmo ano -- ele espera a entressafra. E por isso que a demissao aqui
//     nao acaba a carreira (como acaba no modo SAF do 38 Brasil): ela abre uma
//     janela de procura.

import { makeRng, clamp } from './rng.js';

export const JOB = {
  // Quantas rodadas de temporada antes de a diretoria comecar a cobrar.
  graceRounds: 15,
  // Distancia da meta (em pontos percentuais de aproveitamento) que dispara o
  // risco de demissao no meio da temporada.
  midSeasonMargin: 0.14,
  // Reputacao inicial de quem esta comecando.
  startingReputation: 45,
};

/** Meta de aproveitamento do clube, a partir da forca do elenco. */
export function seasonTarget(team, allTeams) {
  const forca = (t) => {
    const top8 = [...t.roster].sort((a, b) => b.overall - a.overall).slice(0, 8);
    return top8.reduce((a, p) => a + p.overall, 0) / Math.max(1, top8.length);
  };
  const minha = forca(team);
  const todas = allTeams.map(forca).sort((a, b) => a - b);
  const pior = todas[0];
  const melhor = todas[todas.length - 1];
  const posicao = melhor === pior ? 0.5 : (minha - pior) / (melhor - pior);
  // O pior elenco da liga tem meta de 25% de vitorias; o melhor, de 70%.
  return Math.round((0.25 + posicao * 0.45) * 100) / 100;
}

export function targetLabel(target) {
  if (target >= 0.62) return 'brigar pelo titulo';
  if (target >= 0.52) return 'ir bem nos playoffs';
  if (target >= 0.42) return 'entrar nos playoffs';
  if (target >= 0.33) return 'brigar por vaga';
  return 'desenvolver os jovens';
}

/**
 * A diretoria esta satisfeita? Devolve -1 (fritura) a +1 (tranquilo).
 * No comeco da temporada ninguem e demitido: `graceRounds` existe para o
 * usuario ter tempo de trabalhar.
 */
export function boardMood({ record, target, round, graceRounds = JOB.graceRounds }) {
  if (!record || record.games === 0) return 1;
  const pct = record.wins / record.games;
  const dif = pct - target;
  // Antes do periodo de graca, a cobranca e suavizada.
  const peso = round <= graceRounds ? 0.35 : 1;
  return clamp((dif / 0.25) * peso, -1, 1);
}

export function moodLabel(mood) {
  if (mood >= 0.4) return { text: 'Diretoria satisfeita', cls: 'pos' };
  if (mood >= 0) return { text: 'Diretoria tranquila', cls: '' };
  if (mood >= -0.4) return { text: 'Diretoria incomodada', cls: 'warn' };
  if (mood >= -0.75) return { text: 'Voce esta na corda bamba', cls: 'neg' };
  return { text: 'Demissao iminente', cls: 'neg' };
}

/**
 * Deve demitir agora, no meio da temporada?
 * Exige tres coisas ao mesmo tempo: passar do periodo de graca, estar bem
 * abaixo da meta, e o sorteio. Sem o sorteio, a demissao vira formula e o
 * usuario aprende a burlar; com ele, a corda bamba assusta de verdade.
 */
export function shouldFireMidSeason({ record, target, round, seed, season }) {
  if (round <= JOB.graceRounds || !record || record.games < 10) return false;
  const pct = record.wins / record.games;
  const dif = target - pct;
  if (dif < JOB.midSeasonMargin) return false;
  const chance = clamp((dif - JOB.midSeasonMargin) * 1.4, 0, 0.35);
  return makeRng(`demissao|${seed}|${season}|${round}`)() < chance;
}

/** Demite no fim da temporada quem ficou muito abaixo da meta. */
export function shouldFireEndOfSeason({ record, target, madePlayoffs, seed, season }) {
  if (!record || !record.games) return false;
  const pct = record.wins / record.games;
  const dif = target - pct;
  if (madePlayoffs && dif < 0.1) return false;
  if (dif < 0.06) return false;
  const chance = clamp(0.2 + dif * 1.6, 0, 0.85);
  return makeRng(`demissaofim|${seed}|${season}`)() < chance;
}

// ---------------------------------------------------------------- reputacao

/**
 * Reputacao 0..100. E ela que decide quais clubes te procuram depois.
 * Titulo pesa muito, demissao pesa, e uma temporada acima da meta pesa pouco --
 * carreira de tecnico se constroi devagar e desaba rapido.
 */
export function updateReputation(current, { trophy, record, target, fired }) {
  let rep = current ?? JOB.startingReputation;
  const pct = record && record.games ? record.wins / record.games : 0.5;
  const dif = pct - (target ?? 0.5);

  if (trophy === 'liga') rep += 18;
  else if (trophy === 'conferencia') rep += 9;
  else if (trophy === 'playoffs') rep += 4;
  else if (trophy === 'campanha') rep += 5;

  rep += clamp(dif * 22, -10, 10);
  if (fired) rep -= 12;
  return Math.round(clamp(rep, 1, 99));
}

export function reputationLabel(rep) {
  if (rep >= 82) return 'nome consagrado';
  if (rep >= 66) return 'tecnico requisitado';
  if (rep >= 50) return 'nome respeitado';
  if (rep >= 34) return 'conhecido no meio';
  if (rep >= 18) return 'precisa se provar';
  return 'quase esquecido';
}

// ------------------------------------------------------------- vaga de emprego

/**
 * Clubes que procuram tecnico e aceitariam voce.
 * Clube grande so chama quem tem nome; clube pequeno abre a porta para quem
 * esta recomecando. E o `fit` do 38 Brasil, na escala do basquete.
 */
export function jobOffers({ teams, standings, currentClubId, reputation, seed, season, count = 3 }) {
  const rng = makeRng(`vagas|${seed}|${season}`);
  const candidatos = teams
    .filter((t) => t.id !== currentClubId)
    .map((team) => {
      const target = seasonTarget(team, teams);
      const row = standings?.[team.id];
      const pct = row && row.games ? row.wins / row.games : 0.5;
      // Prestigio do clube: elenco bom + campanha boa = vaga disputada.
      const prestigio = clamp(target * 90 + (pct - 0.5) * 40, 5, 99);
      // Um clube procura tecnico com reputacao perto da propria estatura.
      const distancia = Math.abs(prestigio - reputation);
      const interesse = clamp(100 - distancia * 1.5 + rng.normal() * 12, 0, 100);
      return { team, target, prestigio: Math.round(prestigio), interesse: Math.round(interesse) };
    })
    .filter((c) => c.interesse > 45)
    .sort((a, b) => b.interesse - a.interesse);

  /* ⚠️ NUNCA devolver lista vazia.
     O filtro de interesse compara o prestigio do clube com a reputacao do
     tecnico. Um tecnico muito acima -- ou muito abaixo -- da media da liga
     pode nao passar em clube nenhum, e ai a carreira TRAVA sem nenhuma tela
     dizendo o que fazer. Se ninguem se interessou, entram os clubes de menor
     prestigio: sao justamente os que aceitam qualquer tecnico livre. */
  const lista = candidatos.length ? candidatos : teams
    .filter((t) => t.id !== currentClubId)
    .map((team) => ({ team, target: seasonTarget(team, teams), prestigio: Math.round(seasonTarget(team, teams) * 90), interesse: 46 }))
    .sort((x, y) => x.prestigio - y.prestigio);

  return lista.slice(0, count).map((c) => ({
    teamId: c.team.id,
    teamName: c.team.name,
    prestigio: c.prestigio,
    interesse: c.interesse,
    target: c.target,
    targetText: targetLabel(c.target),
  }));
}
